import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';

type Cursor = 'col-resize' | 'row-resize';

/**
 * Releases the page from a drag: its cursor and text selection.
 */
function releasePage() {
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
}

/**
 * A drag handle's pointer handling: the handle holds the pointer for the whole
 * drag (so the drag carries on off the handle, over iframes too), reports each
 * move against its parent's box as it stood when the drag began, and lets the
 * page go if it unmounts mid-drag.
 * @param {Cursor} cursor The page cursor while dragging.
 * @param {Function} onMove Called with each move and the parent's box.
 * @return {object} The handle's ref, whether it is dragging, and its handlers.
 */
export function useDividerDrag(
  cursor: Cursor,
  onMove: (event: { clientX: number; clientY: number }, parent: DOMRect) => void
) {
  const ref = useRef<HTMLDivElement>(null);
  const parentRect = useRef<DOMRect | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => releasePage, []);

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const parent = ref.current?.parentElement;
      if (!parent) return;
      event.preventDefault();
      parentRect.current = parent.getBoundingClientRect();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      document.body.style.cursor = cursor;
      document.body.style.userSelect = 'none';
      setDragging(true);
    },
    [cursor]
  );

  const onPointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      if (parentRect.current) onMove(event, parentRect.current);
    },
    [onMove]
  );

  const end = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!parentRect.current) return;
    parentRect.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    releasePage();
    setDragging(false);
  }, []);

  return {
    ref,
    dragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: end,
      onPointerCancel: end,
    },
  };
}

interface KeyboardStep {
  /** The handle's current value; without one it has no keyboard. */
  value?: number;
  min: number;
  max: number;
  step: number;
  /** The keys that lower and raise the value. */
  decrease: string;
  increase: string;
  onChange: (value: number) => void;
}

/**
 * A separator's arrow-key resizing: steps its known value within bounds.
 * @param {KeyboardStep} options The value, bounds, step and keys.
 * @return {Function | undefined} The key handler, without a known value none.
 */
export function useKeyboardStep({
  value,
  min,
  max,
  step,
  decrease,
  increase,
  onChange,
}: KeyboardStep) {
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (value === undefined) return;
      const direction =
        event.key === decrease ? -1 : event.key === increase ? 1 : 0;
      if (direction === 0) return;
      event.preventDefault();
      onChange(Math.max(min, Math.min(max, value + direction * step)));
    },
    [value, min, max, step, decrease, increase, onChange]
  );
  return value === undefined ? undefined : onKeyDown;
}
